import { Link } from "react-router";

import { WorkThumb } from "../lib/WorkThumb.tsx";
import { OndoCode } from "../lib/OndoCode.tsx";
import { SOLD_COPY } from "./soldWorks.constants.ts";
import type { PlacedRow as Row } from "./soldWorks.model.ts";

/**
 * One work the gallery has placed.
 *
 * The inventory row's twin, and the differences are all one difference: that row
 * is about an arrangement and this one is about a SALE. So where the inventory
 * prints terms and an asking price, this prints a buyer, what the work made, and
 * where its title now is — and it carries a right-hand column the inventory has
 * no use for, because only a sale can bind anybody afterwards.
 *
 * Built out of the same objects — `WorkThumb`, `OndoCode`, a `Link` to the
 * same work record every other portal opens. There is no gallery-shaped copy of
 * that record: what a gallery may do to a work it sold is decided in SQL
 * (`ondo.placed_work_ids()` feeds READ and nothing else), and a second screen
 * would be a second opinion.
 */
export function PlacedRow({
  row,
  confirming,
  busy,
  onAsk,
  onCancel,
  onWithdraw,
}: {
  row: Row;
  /** This row is armed — the second press will end the sale. */
  confirming: boolean;
  /** A write is in flight for this row. */
  busy: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onWithdraw: () => void;
}) {
  return (
    <li className={`gallery__row gallery__row--${row.titleState}`}>
      <Link className="gallery__row-open" to={row.href}>
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />

        <div className="gallery__row-body">
          <h3 className="gallery__row-title">
            {row.title}
            {/* The same badge the inventory draws, off the same function, and
                saying the same thing as the mark below it — see `toRow`. */}
            <span className={`gallery__code gallery__code--${row.code}`}>
              {row.codeLabel}
            </span>
            {row.justSold && (
              <span className="gallery__just-sold">{SOLD_COPY.justSold}</span>
            )}
          </h3>

          <p className="gallery__row-identity">
            {row.identity}
            {" · "}
            {row.sale}
            {" · "}
            <strong className="gallery__row-price">{row.price}</strong>
          </p>

          {/* WHERE TITLE IS, said in its own line rather than folded into the
              sentence above. It is the one fact on this row that is about the
              REGISTRY rather than about the sale, and a reader scanning a ledger
              for what is outstanding should not have to parse a sentence to find
              it. */}
          <p className={`gallery__title-state gallery__title-state--${row.titleState}`}>
            {row.titleLabel}
          </p>
        </div>

        {/* The code itself, drawn, and UNCHANGED BY THE SALE — the bars are
            the work's identity and only the colour moves. See
            SOLD_COPY.codeNote for why there is no `·R` suffix either. */}
        <OndoCode code={row.ondoCode} facts={row.codeFacts} />
      </Link>

      {/* OUTSIDE the link, matching the inventory row — and now carrying a
          control as well as the covenant panel, which is what the comment here
          used to say there was none of. A panel or a button nested inside an
          anchor would navigate on a click that missed a word by two pixels, and
          the panel is the part of this row somebody actually reads closely. */}
      <div className="gallery__row-action">
        {row.covenant ? (
          <div className={`gallery__covenant gallery__covenant--${row.covenant.state}`}>
            <span className="gallery__covenant-state">{row.covenant.label}</span>
            {row.covenant.remaining && (
              <span className="gallery__covenant-remaining">{row.covenant.remaining}</span>
            )}
          </div>
        ) : (
          /* NOTHING, rather than "no covenant". A row announcing an absence on
             every line makes the terms harder to see, not easier — and most
             sales carry none. */
          <span className="gallery__covenant gallery__covenant--none" aria-hidden="true" />
        )}

        {/* TWO PRESSES, the shape `deleteRoom` uses. A sale is the most
            consequential row on this screen and the first press is easy to make
            by accident; the second is not, and the sentence between them says
            what actually happens rather than asking "are you sure". */}
        {row.canWithdraw &&
          (confirming ? (
            <span className="gallery__withdraw-confirm">
              <span className="gallery__withdraw-note">{SOLD_COPY.withdrawNote}</span>
              <button
                type="button"
                className="gallery__withdraw-yes"
                disabled={busy}
                onClick={onWithdraw}
              >
                {SOLD_COPY.withdrawConfirm}
              </button>
              <button type="button" className="gallery__withdraw-no" onClick={onCancel}>
                {SOLD_COPY.withdrawCancel}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="gallery__withdraw"
              disabled={busy}
              onClick={onAsk}
            >
              {SOLD_COPY.withdraw}
            </button>
          ))}
      </div>
    </li>
  );
}
